import { RouteError } from "./RouteState";
import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Dashboard } from "@/pages/Dashboard";
import { ComingSoon } from "@/pages/ComingSoon";
import { Login } from "@/pages/Login";
import { Signup } from "@/pages/Signup";
import { RequireAdmin, RequireAuth, RequirePerm } from "./guards";
import {
  BizGate,
  LegacyMyRedirect,
  LegacyTenantRedirect,
  SpaceIndexRedirect,
  TenantGate,
  TenantHomeRedirect,
  TenantNavigate,
} from "./TenantRoutes";
import { LEGACY_PATH_KEYS } from "@/domain/menus";
import { SpaceProvider } from "@/contexts/SpaceContext";
import { MySpaceAll, MySpaceHome, SpacePlaceholder } from "@/pages/MySpace";
import { MySpaceTasks } from "@/pages/MySpace/Tasks";
import { GroupDenied } from "@/pages/Group/GroupDenied";
import {
  GroupAccessLogRoute,
  GroupAdminsRoute,
  GroupShellRoute,
  GroupUsersRoute,
  GroupWorkdayRoute,
  GroupNotifyRoute,
  GroupBrandRoute,
  GroupRanksRoute,
  GroupCompanyRoute,
} from "@/pages/Group/GroupRoutes";

// 메뉴 첫 진입 속도 개선을 위해 큰 페이지는 lazy 로딩 → 별도 청크
const Mix = lazy(() => import("@/pages/Mix").then((m) => ({ default: m.Mix })));
const RawMaterials = lazy(() => import("@/pages/RawMaterials").then((m) => ({ default: m.RawMaterials })));
// 공장 배치도는 pages/PlantView에 내부 보관. 배포 제외를 위해 import/라우트 연결 금지.
const Dispatch = lazy(() => import("@/pages/Dispatch").then((m) => ({ default: m.Dispatch })));
const ShipmentStats = lazy(() =>
  import("@/pages/ShipmentStats").then((m) => ({ default: m.ShipmentStats })),
);
const MaterialForecast = lazy(() =>
  import("@/pages/MaterialForecast").then((m) => ({ default: m.MaterialForecast })),
);
const ShipSim = lazy(() => import("@/pages/ShipSim").then((m) => ({ default: m.ShipSim })));
// 단가표 = MarketPage(신규). 이전 MarketPanel(@/pages/Market)은 보존만, 라우트 미연결
const Market = lazy(() => import("@/pages/Market/MarketPage").then((m) => ({ default: m.MarketPage })));
const Quote = lazy(() => import("@/pages/Quote").then((m) => ({ default: m.Quote })));
const Sales = lazy(() => import("@/pages/Sales").then((m) => ({ default: m.Sales })));
const Payroll = lazy(() => import("@/pages/Payroll").then((m) => ({ default: m.Payroll })));
// 원가 관리 개편(월별·연간 원가·계정 설정)은 구조 미확정 → 운영 배포본은 9/10 옛 화면(pages/CostLegacy).
// dev 서버(5173)는 계속 새 화면으로 작업한다. 개편을 운영에 낼 때 true 로 바꾸고 CostLegacy 폴더를 지운다.
const COST_LEDGER_RELEASED = false;
const Cost = COST_LEDGER_RELEASED || import.meta.env.DEV
  ? lazy(() => import("@/pages/Cost").then((m) => ({ default: m.Cost })))
  : lazy(() => import("@/pages/CostLegacy").then((m) => ({ default: m.CostLegacy })));
const Settings = lazy(() => import("@/pages/Settings").then((m) => ({ default: m.Settings })));
// 석산 사업부지 — 등기부 지도 정적 앱(web/public/land-registry) iframe. 게이트는 menus.ts 와 같게(aggregate.view)
const LandRegistry = lazy(() => import("@/pages/LandRegistry").then((m) => ({ default: m.LandRegistry })));
// 외부 직원 급여명세서 열람 포털 — 비로그인(RequireAuth 밖). 익명 인증 + 토큰 게이트.
const PayslipPortal = lazy(() =>
  import("@/pages/PayslipPortal").then((m) => ({ default: m.PayslipPortal })),
);
const SettingsDemo = lazy(() =>
  import("@/pages/SettingsDemo").then((m) => ({ default: m.SettingsDemo })),
);

function LazyFallback() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 40,
        color: "var(--c-text-muted)",
        fontSize: "var(--fs-base)",
      }}
    >
      불러오는 중…
    </div>
  );
}

function lazyRoute(El: React.ComponentType) {
  return (
    <Suspense fallback={<LazyFallback />}>
      <El />
    </Suspense>
  );
}

const COMING_SOON_KEYS = [] as const;

export const router = createBrowserRouter([
  { path: "/login", errorElement: <RouteError />, element: <Login /> },
  { path: "/signup", errorElement: <RouteError />, element: <Signup /> },
  // 급여명세서 열람 포털 — 로그인 불필요(외부 직원). 발송 링크 https://erp.jgind.co.kr/p/{token}
  { path: "/p/:token", errorElement: <RouteError />, element: lazyRoute(PayslipPortal) },
  {
    path: "/",
    errorElement: <RouteError />,
    element: <RequireAuth />,
    children: [
      {
        // ── 통합관리 /group/* — 법인 공간 밖 독립 페이지(자체 사이드바, 상단 공간 바는 선택 없음). 전체 관리자만 ──
        //    공간 바·계정 메뉴가 useSpace 를 쓰므로 SpaceProvider 는 따로 감싼다. 권한 없음 = B-6b(GroupDenied)
        path: "group",
        element: <SpaceProvider><GroupShellRoute /></SpaceProvider>,
        children: [
          {
            element: <RequireAdmin denied={<GroupDenied />} />,
            children: [
              { index: true, element: <Navigate to="users" replace /> },
              { path: "users", element: <GroupUsersRoute /> },
              // 부서·권한은 법인 환경설정(사업부별)으로 옮김(12 §16-2) — 옛 주소는 소속·관리자 지정으로
              { path: "roles", element: <Navigate to="../admins" replace /> },
              { path: "ranks", element: <GroupRanksRoute /> },
              { path: "admins", element: <GroupAdminsRoute /> },
              { path: "workday", element: <GroupWorkdayRoute /> },
              { path: "notify", element: <GroupNotifyRoute /> },
              { path: "company", element: <GroupCompanyRoute /> },
              { path: "brand", element: <GroupBrandRoute /> },
              { path: "access-log", element: <GroupAccessLogRoute /> },
              { path: "*", element: <Navigate to="users" replace /> },
            ],
          },
        ],
      },
      {
        // B′ 미리보기: 공간(내 업무/중앙/경원) 상태는 Layout 바깥 Provider 가 갖는다(헤더·사이드바·페이지 공유)
        element: (
          <SpaceProvider>
            <Layout />
          </SpaceProvider>
        ),
        children: [
          // 루트 "/" = 기본 공간 홈(/my 또는 /c/{tenant}/{biz}). 법인 주소 /c/:tenant/… (D-01) → 법인 › 사업 2단계(D-26, 10-22)
          { index: true, element: <SpaceIndexRedirect /> },
          // ── 내 업무 공간(B′ 미리보기) — 로그인만으로 진입. 휴가·지출/내 급여는 자리만 ──
          { path: "my", element: <MySpaceHome /> },
          { path: "my/all", element: <MySpaceAll /> },
          { path: "my/tasks", element: <MySpaceTasks /> },
          { path: "my/leave", element: <SpacePlaceholder /> },
          { path: "my/pay", element: <SpacePlaceholder /> },
          {
            // 급여 관리(통합) — 회계 담당자가 두 법인 급여를 한 화면에서(D-27). 화면은 급여관리 그대로.
            path: "my/payroll",
            element: <RequirePerm perm="payroll.import" />,
            children: [{ index: true, element: lazyRoute(Payroll) }],
          },
          // 옛 내 업무 주소(/me…) → /my… (2026-10-22 D-26)
          { path: "me/*", element: <LegacyMyRedirect /> },
          {
            // ── 법인 공간 — /c/joongang/rmc/sales(사업 메뉴) · /c/joongang/payroll(법인 공통). 화이트리스트·소속은 TenantGate ──
            path: "c/:tenant",
            element: <TenantGate />,
            children: [
              // /c/{t} → 기본 사업 홈 /c/{t}/{biz}
              { index: true, element: <TenantHomeRedirect /> },
              // ── 법인 공통 메뉴(D-26) — /c/{t}/{key}. 정적 경로라 아래 :biz 보다 먼저 매칭된다 ──
              {
                path: "cost",
                element: <RequirePerm perm="cost.view" />,
                children: [{ index: true, element: lazyRoute(Cost) }],
              },
              {
                path: "payroll",
                element: <RequirePerm perm="payroll.view" />,
                children: [{ index: true, element: lazyRoute(Payroll) }],
              },
              {
                path: "settings",
                // 로그인(RequireAuth)·법인 소속(TenantGate)만 확인. 진입 판정은 화면이 한다(12 §8-C) —
                // 전체 admin·그 법인 관리자 = 허브, 일반 직원 = C-3 안내(+ 내 계정·환경 열기).
                // 섹션별 편집 권한은 각 컴포넌트가 adminOnly 로 추가 제어.
                children: [{ index: true, element: lazyRoute(Settings) }],
              },
              {
                // ── 사업 메뉴 — /c/{t}/{biz}/{key}. BizGate 가 그 법인 사업인지 확인, 아니면 첫 세그먼트를
                //    메뉴로 보고 기본 사업 아래로 보낸다(/c/joongang/sales → /c/joongang/rmc/sales) ──
                path: ":biz",
                element: <BizGate />,
                children: [
                  // 사업 홈(대시보드). 옛 /dashboard 북마크와 같은 이름의 …/dashboard 도 홈으로.
                  { index: true, element: <Dashboard /> },
                  { path: "dashboard", element: <TenantNavigate to="" /> },
                  {
                    // 경원 전용 차량·운반비 — 자리만(comingSoon). 게이트는 출하 권한 그대로(menus.ts 와 같게)
                    path: "vehicle-cost",
                    element: <RequirePerm perm={["shipment.view", "dispatch.view"]} />,
                    children: [{ index: true, element: <SpacePlaceholder /> }],
                  },
                  {
                    path: "mix",
                    element: <RequirePerm perm="mix.view" />,
                    children: [{ index: true, element: lazyRoute(Mix) }],
                  },
                  // 옛 원재료단가(구) 메뉴 — 원자재 관리 단가 탭으로 흡수됨. 북마크 호환 리다이렉트.
                  { path: "material", element: <TenantNavigate to="raw-materials?tab=prices" /> },
                  {
                    path: "raw-materials",
                    element: <RequirePerm perm="ledger.view" />,
                    children: [{ index: true, element: lazyRoute(RawMaterials) }],
                  },
                  {
                    path: "dispatch-view",
                    element: <RequirePerm perm={["shipment.view", "dispatch.view"]} />,
                    children: [{ index: true, element: lazyRoute(Dispatch) }],
                  },
                  {
                    path: "shipment-stats",
                    element: <RequirePerm perm={["shipment.view", "dispatch.view"]} />,
                    children: [{ index: true, element: lazyRoute(ShipmentStats) }],
                  },
                  {
                    path: "material-forecast",
                    element: <RequirePerm perm="forecast.view" />,
                    children: [{ index: true, element: lazyRoute(MaterialForecast) }],
                  },
                  {
                    path: "ship-sim",
                    element: <RequirePerm perm="ship_sim.view" />,
                    children: [{ index: true, element: lazyRoute(ShipSim) }],
                  },
                  // 옛 "경로계산"(ComingSoon) 북마크 호환
                  { path: "route", element: <TenantNavigate to="ship-sim" /> },
                  {
                    path: "market",
                    element: <RequirePerm perm="prices.view" />,
                    children: [{ index: true, element: lazyRoute(Market) }],
                  },
                  {
                    path: "quote",
                    element: <RequirePerm perm="quote.view" />,
                    children: [{ index: true, element: lazyRoute(Quote) }],
                  },
                  {
                    path: "sales",
                    element: <RequirePerm perm="sales.view" />,
                    children: [{ index: true, element: lazyRoute(Sales) }],
                  },
                  {
                    // 석산 전용(menus.ts biz: ["aggregate"]). 다른 사업 주소로 오면 BizGate 가 그 사업 첫 메뉴로 돌린다
                    path: "land-registry",
                    element: <RequirePerm perm="aggregate.view" />,
                    children: [{ index: true, element: lazyRoute(LandRegistry) }],
                  },
                  { path: "*", element: <ComingSoon pageKey="not-found" /> },
                ],
              },
            ],
          },
          // 옛 주소(법인 구분 없음) → /c/joongang/rmc/… 또는 /c/joongang/… — 북마크·알림 링크·푸시 딥링크(…/dispatch-view) 보호
          ...LEGACY_PATH_KEYS.map((key) => ({ path: `${key}/*`, element: <LegacyTenantRedirect /> })),
          {
            // 옛 헤더 ⚙ 설정 모달 디자인 이식 — 참고/실험용 (기존 /settings 와 별개)
            path: "settings-demo",
            children: [{ index: true, element: lazyRoute(SettingsDemo) }],
          },
          ...COMING_SOON_KEYS.map((key) => ({
            path: key,
            element: <ComingSoon pageKey={key} />,
          })),
          { path: "*", element: <ComingSoon pageKey="not-found" /> },
        ],
      },
    ],
  },
]);
